// Epoch 23 v2: Feature Flags Hook - DEFENSIVE IMPLEMENTATION
// Version: 2.1 (Enhanced with Opus recommendations)
// Date: August 4, 2025
// CRITICAL: This hook is designed to be SAFE and prevent v1 issues

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase/client';

interface FeatureFlag {
  flag_name: string;
  enabled: boolean;
  description?: string;
  updated_at?: string;
}

interface FeatureFlagsReturn {
  flags: Record<string, boolean>;
  isLoading: boolean;
  error: string | null;
  refreshFlags: () => Promise<void>;
  isEnabled: (flagName: string) => boolean;
}

// OPUS ENHANCEMENT: Cache for feature flags with 5-minute TTL
const flagCache = new Map<string, { data: FeatureFlag[], timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Default flags (ALL DISABLED for safety)
const DEFAULT_FLAGS: Record<string, boolean> = {
  TRIAL_SIGNUP_ENABLED: false,
  TRIAL_CAMPAIGN_LIMITS: false,
  TRIAL_BANNER_DISPLAY: false,
  TRIAL_EMAIL_AUTOMATION: false,
  TRIAL_CONVERSION_PROMPTS: false,
};

/**
 * DEFENSIVE: Feature flags hook with extensive error handling
 * This hook NEVER modifies existing user sessions or billing hooks
 */
export const useFeatureFlags = (): FeatureFlagsReturn => {
  const [flags, setFlags] = useState<Record<string, boolean>>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const fetchInProgressRef = useRef(false);
  const retryCountRef = useRef(0);

  // DEFENSIVE: Prevent race conditions and multiple simultaneous fetches
  const fetchFlags = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (fetchInProgressRef.current) {
      console.log('🔒 Feature flag fetch already in progress, skipping');
      return;
    }

    fetchInProgressRef.current = true;

    try {
      setIsLoading(true);
      setError(null);

      // OPUS ENHANCEMENT: Check cache first
      const cached = flagCache.get('all_flags');
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < CACHE_TTL) {
        console.log('📦 Using cached feature flags (age: ' + Math.round((now - cached.timestamp) / 1000) + 's)');
        
        if (isMountedRef.current) {
          const flagMap = cached.data.reduce((acc, flag) => {
            acc[flag.flag_name] = flag.enabled;
            return acc;
          }, {} as Record<string, boolean>);
          
          // Merge with defaults to ensure all flags exist
          setFlags({ ...DEFAULT_FLAGS, ...flagMap });
          setIsLoading(false);
        }
        
        fetchInProgressRef.current = false;
        return;
      }

      console.log('🌐 Fetching feature flags from API');

      // Fetch from Edge Function
      const { data, error: fetchError } = await supabase.functions.invoke('feature-flags', {
        method: 'GET',
      });

      if (fetchError) {
        throw new Error(`Failed to fetch feature flags: ${fetchError.message}`);
      }

      if (!Array.isArray(data)) {
        console.warn('⚠️ Invalid feature flags response format, using defaults');
        throw new Error('Invalid feature flags response format');
      }

      // DEFENSIVE: Validate each flag
      const validatedData = data.filter(flag => 
        flag && 
        typeof flag.flag_name === 'string' && 
        typeof flag.enabled === 'boolean'
      );

      // Update cache
      flagCache.set('all_flags', { data: validatedData, timestamp: now });

      // Convert to flag map
      const flagMap = validatedData.reduce((acc: Record<string, boolean>, flag: FeatureFlag) => {
        acc[flag.flag_name] = flag.enabled;
        return acc;
      }, {});

      // DEFENSIVE: Always merge with defaults
      const mergedFlags = { ...DEFAULT_FLAGS, ...flagMap };

      console.log('✅ Feature flags loaded:', mergedFlags);
      
      if (isMountedRef.current) {
        setFlags(mergedFlags);
        retryCountRef.current = 0; // Reset retry count on success
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load feature flags';
      console.error('❌ Feature flags error:', err);
      
      // OPUS ENHANCEMENT: Retry logic with exponential backoff
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const delay = RETRY_DELAY * Math.pow(2, retryCountRef.current - 1);
        console.log(`🔄 Retrying feature flag fetch (attempt ${retryCountRef.current}/${MAX_RETRIES}) in ${delay}ms`);
        
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchInProgressRef.current = false;
            fetchFlags();
          }
        }, delay);
        
        return; // Don't set error state during retries
      }
      
      if (isMountedRef.current) {
        setError(errorMessage);
        // CRITICAL: Use safe defaults on error
        setFlags(DEFAULT_FLAGS);
        console.log('⚠️ Using default flags due to error:', DEFAULT_FLAGS);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
      fetchInProgressRef.current = false;
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchFlags();

    // Cleanup
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchFlags]);

  // DEFENSIVE: Helper function to check if flag is enabled
  const isEnabled = useCallback((flagName: string): boolean => {
    // Always return false for unknown flags (defensive)
    const enabled = flags[flagName] === true;
    
    // Log critical flag checks for monitoring
    if (flagName.startsWith('TRIAL_') && enabled) {
      console.log(`🏁 Trial feature flag checked: ${flagName} = ${enabled}`);
    }
    
    return enabled;
  }, [flags]);

  return {
    flags,
    isLoading,
    error,
    refreshFlags: fetchFlags,
    isEnabled,
  };
};

/**
 * DEFENSIVE: Single flag check hook
 * Use this when you only need to check one specific flag
 */
export const useFeatureFlag = (flagName: string): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagName);
};

/**
 * OPUS ENHANCEMENT: Utility function to check flags without hooks
 * Use this in non-component contexts
 */
export const checkFeatureFlag = async (flagName: string): Promise<boolean> => {
  try {
    // Check cache first
    const cached = flagCache.get('all_flags');
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_TTL) {
      const flag = cached.data.find(f => f.flag_name === flagName);
      return flag?.enabled || false;
    }

    // Fetch if not cached
    const { data, error } = await supabase.functions.invoke(`feature-flags?flag=${flagName}`, {
      method: 'GET',
    });

    if (error || !data) {
      console.error('Failed to check feature flag:', error);
      return false; // Default to disabled
    }

    return data.enabled === true;
  } catch (error) {
    console.error('Error checking feature flag:', error);
    return false; // DEFENSIVE: Always default to disabled on error
  }
};

// OPUS ENHANCEMENT: Export cache clear utility for testing
export const clearFeatureFlagCache = () => {
  flagCache.clear();
  console.log('🧹 Feature flag cache cleared');
};